/**
 * 🆕 [Phase 84 — 2026-06-23] DataHealthBlock — Santé des données (BT orphelins)
 *
 * Bloc Owner-only intégré dans la Vue d'ensemble → section PRQC.
 * Transforme le log [BT_ORPHELIN] (Phase 83) en outil de nettoyage proactif.
 *
 * Endpoints consommés :
 *   GET    /api/admin/data-health/bt-orphelins?months_back=12
 *   POST   /api/admin/data-health/recreate-dossier/{dossier_id}
 *   DELETE /api/admin/data-health/bt-zombie/{bt_id}
 *   DELETE /api/admin/data-health/bt-zombies-bulk?confirm=true
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Loader2, AlertTriangle, RefreshCw, Trash2, Search } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;


export function DataHealthBlock({ deptView }) {
  const [loading, setLoading] = useState(false);
  const [orphans, setOrphans] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [monthsBack, setMonthsBack] = useState(12);
  const [filterMois, setFilterMois] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [actionInFlight, setActionInFlight] = useState(null); // bt_id or "bulk"

  const fetchOrphans = async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/admin/data-health/bt-orphelins?months_back=${monthsBack}`
      );
      setOrphans(r.data.orphelins || []);
    } catch (e) {
      console.error("[PHASE_84_FRONTEND] fetch error:", e);
      toast.error("Impossible de charger la santé des données");
      setOrphans([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchOrphans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthsBack]);

  // Calcul des mois uniques pour le filtre
  const moisUniques = useMemo(() => {
    const set = new Set();
    orphans.forEach(o => (o.mois_affectes || []).forEach(m => set.add(m)));
    return Array.from(set).sort();
  }, [orphans]);

  // Filtrage côté client (mois + search)
  const filteredOrphans = useMemo(() => {
    return orphans.filter(o => {
      if (filterMois && !(o.mois_affectes || []).includes(filterMois)) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matches = (o.bt_numero || "").toLowerCase().includes(q)
          || (o.dossier_id || "").toLowerCase().includes(q)
          || (o.nom_dossier || "").toLowerCase().includes(q);
        if (!matches) return false;
      }
      return true;
    });
  }, [orphans, filterMois, searchTerm]);

  const handleRecreateDossier = async (dossier_id) => {
    setActionInFlight(`recreate-${dossier_id}`);
    try {
      await axios.post(`${API_URL}/api/admin/data-health/recreate-dossier/${dossier_id}`);
      toast.success(`Dossier ${dossier_id} créé. À renseigner.`);
      await fetchOrphans();
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur lors de la recréation du dossier";
      toast.error(msg);
    }
    setActionInFlight(null);
  };

  const handleDeleteBt = async (bt_id) => {
    if (!window.confirm("Supprimer ce BT zombie ? Cette action est irréversible.")) return;
    setActionInFlight(`delete-${bt_id}`);
    try {
      await axios.delete(`${API_URL}/api/admin/data-health/bt-zombie/${bt_id}`);
      toast.success(`BT ${bt_id} supprimé.`);
      await fetchOrphans();
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur lors de la suppression du BT";
      toast.error(msg);
    }
    setActionInFlight(null);
  };

  const handleBulkDelete = async () => {
    setActionInFlight("bulk");
    setShowBulkConfirm(false);
    try {
      const r = await axios.delete(
        `${API_URL}/api/admin/data-health/bt-zombies-bulk?confirm=true&months_back=${monthsBack}`
      );
      toast.success(`${r.data.deleted_count} BT zombies supprimés.`);
      await fetchOrphans();
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur lors de la suppression bulk";
      toast.error(msg);
    }
    setActionInFlight(null);
  };

  // N'afficher que pour PRQC ou global (le check Owner-only se fait côté parent)
  if (deptView !== "PRQC" && deptView !== "global") return null;

  const total = orphans.length;
  const healthLabel = total === 0 ? "🩺 Santé des données" : `🚨 ${total} BT orphelins détectés`;
  const healthColor = total === 0 ? "text-emerald-600" : "text-red-600";

  return (
    <>
      <Card data-testid="data-health-block">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-base">
            <div className="flex items-center gap-2">
              <Heart className={`h-5 w-5 ${healthColor}`} aria-hidden="true" />
              <span data-testid="data-health-counter" className={healthColor}>
                {healthLabel}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchOrphans}
              disabled={loading}
              data-testid="data-health-refresh-btn"
              aria-label="Rafraîchir la santé des données"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Bons de travail pointant vers un dossier supprimé ou inexistant
            <span className="text-xs text-slate-400"> (12 derniers mois)</span>
          </p>
          {total > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDetail(true)}
              data-testid="data-health-detail-btn"
            >
              Voir le détail ({total})
            </Button>
          )}
          {total === 0 && !loading && (
            <p className="text-xs text-emerald-600">
              ✅ Aucun BT zombie détecté. Base de données saine.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Modal détail */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent
          className="max-w-4xl max-h-[85vh] overflow-y-auto"
          data-testid="data-health-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" aria-hidden="true" />
              BT orphelins — Détail ({filteredOrphans.length}/{total})
            </DialogTitle>
            <DialogDescription>
              Bons de travail pointant vers un dossier inexistant en DB. Tu peux
              recréer le dossier OU supprimer le BT zombie.
            </DialogDescription>
          </DialogHeader>

          {/* Filtres */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-3">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" aria-hidden="true" />
              <Input
                placeholder="Rechercher (BT numéro / dossier ID)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
                data-testid="data-health-search"
              />
            </div>
            <select
              value={filterMois}
              onChange={(e) => setFilterMois(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm"
              data-testid="data-health-filter-mois"
            >
              <option value="">Tous les mois</option>
              {moisUniques.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowBulkConfirm(true)}
              disabled={total === 0 || actionInFlight === "bulk"}
              data-testid="data-health-bulk-delete-btn"
            >
              {actionInFlight === "bulk" ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" aria-hidden="true" />
              ) : (
                <Trash2 className="h-4 w-4 mr-1" aria-hidden="true" />
              )}
              Supprimer tous ({total})
            </Button>
          </div>

          {/* Tableau */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800">
                <tr>
                  <th className="text-left py-2 px-2">BT Numéro</th>
                  <th className="text-left py-2 px-2">Dossier ID (orphelin)</th>
                  <th className="text-left py-2 px-2">Mois affectés</th>
                  <th className="text-left py-2 px-2">Statut</th>
                  <th className="text-right py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrphans.map(o => (
                  <tr
                    key={o.bt_id}
                    className="border-t"
                    data-testid={`data-health-row-${o.bt_id}`}
                  >
                    <td className="py-2 px-2 font-mono text-xs">{o.bt_numero || o.bt_id?.slice(0, 8)}</td>
                    <td className="py-2 px-2 font-mono text-xs text-amber-700">{o.dossier_id}</td>
                    <td className="py-2 px-2">{(o.mois_affectes || []).join(", ") || "—"}</td>
                    <td className="py-2 px-2">{o.statut}</td>
                    <td className="py-2 px-2 text-right space-x-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRecreateDossier(o.dossier_id)}
                        disabled={actionInFlight === `recreate-${o.dossier_id}`}
                        data-testid={`recreate-dossier-${o.dossier_id}`}
                      >
                        {actionInFlight === `recreate-${o.dossier_id}` ? (
                          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                        ) : (
                          "Recréer dossier"
                        )}
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteBt(o.bt_id)}
                        disabled={actionInFlight === `delete-${o.bt_id}`}
                        data-testid={`delete-bt-${o.bt_id}`}
                      >
                        {actionInFlight === `delete-${o.bt_id}` ? (
                          <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                        ) : (
                          <Trash2 className="h-3 w-3" aria-hidden="true" />
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
                {filteredOrphans.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-4 text-slate-500">
                      Aucun BT orphelin {searchTerm || filterMois ? "correspondant aux filtres" : ""}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog confirmation bulk delete */}
      <AlertDialog open={showBulkConfirm} onOpenChange={setShowBulkConfirm}>
        <AlertDialogContent data-testid="data-health-bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Es-tu sûre ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est <strong>irréversible</strong>. {total} BT zombies
              seront définitivement supprimés de la base de données.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="data-health-bulk-cancel">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="data-health-bulk-confirm"
            >
              Oui, supprimer {total} BT
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default DataHealthBlock;
